// src/api.ts — fetch wrapper. All API calls go through here.

import { writeAuthHint } from "./auth";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// Mirror of PUBLIC_AUTH_PATHS in src/auth.ts -- these are the pages we
// must not redirect AWAY from on 401, or we'd loop. apiFetch is called
// from inside LoginScreen's PIN submit and similar public-route flows.
const PUBLIC_AUTH_PATHS: ReadonlySet<string> = new Set([
  "/login",
  "/register",
  "/forgot-pin",
]);

function redirectToLogin() {
  if (PUBLIC_AUTH_PATHS.has(window.location.pathname)) return;
  // Vacancies V2: the public Work With Us site is anonymous — never bounce it.
  if (window.location.pathname.startsWith("/work-with-us")) return;
  const hint = localStorage.getItem("nymr_auth_hint");
  const returnTo = window.location.pathname + window.location.search;
  if (hint === "sso") {
    // Express path for returning SSO users (see src/auth.ts for the full
    // decision table). Active token = auto-login; no detour through /login.
    window.location.replace(`/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`);
  } else {
    // PIN users, first-time visitors, post-logout, or new device -- send
    // them to the /login choice page so they pick PIN vs SSO themselves.
    window.location.replace(`/login?return=${encodeURIComponent(returnTo)}`);
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const url = path.startsWith("/api") ? path : `/api${path.startsWith("/") ? path : `/${path}`}`;
  const res = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    ...init,
  });

  if (res.status === 401) {
    redirectToLogin();
    // Throw so the caller's promise rejects rather than resolving with stale data.
    throw new ApiError(401, null, "Not authenticated");
  }

  const isJson = (res.headers.get("content-type") ?? "").includes("application/json");
  const body: unknown = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      typeof body === "object" && body !== null && "error" in body && typeof (body as { error: unknown }).error === "string"
        ? (body as { error: string }).error
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, body, message);
  }

  return body as T;
}

// Re-export so callers don't need a second import path.
export { writeAuthHint };
